export const PRO_MODE_FRAME_RATE = 30;
export const PRO_MODE_MAX_BITRATE = 3_000_000;
export const PRO_MODE_FULL_HD_MAX_BITRATE = 5_500_000;

export const PRO_VIDEO_PROFILE_HD = "hd";
export const PRO_VIDEO_PROFILE_FULL_HD = "full-hd";
export const PRO_AUDIO_MODE_RAW = "raw";

const isConstrainedConnection = (connection = {}) => (
  connection.saveData === true
  || ["slow-2g", "2g", "3g"].includes(String(connection.effectiveType || "").toLowerCase())
  || (Number(connection.downlink || 0) > 0 && Number(connection.downlink) < 6)
);

// Network Information is not available in every mobile browser. When it is
// unavailable, use the device display/CPU only as a conservative capability
// signal; LiveKit simulcast remains responsible for adapting each viewer.
export const selectProVideoProfile = ({
  connection = {},
  screenWidth = 0,
  screenHeight = 0,
  pixelRatio = 1,
  hardwareConcurrency = 0,
  isMobile = false,
} = {}) => {
  if (isMobile || isConstrainedConnection(connection)) return PRO_VIDEO_PROFILE_HD;

  const longestDisplayEdge = Math.max(Number(screenWidth) || 0, Number(screenHeight) || 0)
    * Math.max(1, Number(pixelRatio) || 1);
  const capableDisplay = longestDisplayEdge >= 1800;
  const capableCpu = !hardwareConcurrency || Number(hardwareConcurrency) >= 4;
  // Safari and several privacy-focused mobile browsers do not expose the
  // Network Information API. Missing bandwidth data is not evidence that a
  // phone can sustain a 5.5 Mbps Full HD uplink, so default to the safer HD
  // profile unless the browser reports a strong connection explicitly.
  const connectionLooksStrong = connection.effectiveType === "4g"
    && (!Number(connection.downlink || 0) || Number(connection.downlink) >= 10);
  return capableDisplay && capableCpu && connectionLooksStrong
    ? PRO_VIDEO_PROFILE_FULL_HD
    : PRO_VIDEO_PROFILE_HD;
};

export const getBrowserProVideoProfile = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return PRO_VIDEO_PROFILE_HD;
  }
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};
  const mobileUserAgent = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");
  const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches === true;
  return selectProVideoProfile({
    connection,
    screenWidth: window.screen?.width || window.innerWidth,
    screenHeight: window.screen?.height || window.innerHeight,
    pixelRatio: window.devicePixelRatio || 1,
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
    isMobile: mobileUserAgent || coarsePointer,
  });
};

export const getProConnectionReadiness = (profile, connection = {}) => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { level: "offline", label: "Offline", detail: "Connect to Wi-Fi or cellular data" };
  }
  if (isConstrainedConnection(connection)) {
    return {
      level: "limited",
      label: "Connection limited",
      detail: "Using connection-safe HD",
    };
  }
  return profile === PRO_VIDEO_PROFILE_FULL_HD
    ? { level: "excellent", label: "Full HD ready", detail: "Automatic 1080p encoder ceiling" }
    : { level: "good", label: "HD ready", detail: "Automatic 720p encoder ceiling" };
};

// Keep a stable 30 fps ceiling while leaving width, height, and aspect ratio to
// the browser's native camera selection.
export const getProCaptureResolution = (orientation = "auto") => {
  void orientation;
  return { frameRate: PRO_MODE_FRAME_RATE };
};

export const getProVideoOptions = (orientation = "auto", facingMode = "user") => ({
  ...getProCaptureResolution(orientation),
  facingMode,
  frameRate: { ideal: PRO_MODE_FRAME_RATE, max: PRO_MODE_FRAME_RATE },
});

// One native-dimension option avoids repeated camera reacquisition on mobile.
export const getProVideoOptionCandidates = (orientation = "auto", facingMode = "user") => {
  void orientation;
  return [{
    facingMode,
    frameRate: { ideal: PRO_MODE_FRAME_RATE, max: PRO_MODE_FRAME_RATE },
  }];
};

export const getProTrackOrientation = (trackOrSettings) => {
  const settings = typeof trackOrSettings?.getSettings === "function"
    ? trackOrSettings.getSettings()
    : (trackOrSettings || {});
  const width = Number(settings.width || 0);
  const height = Number(settings.height || 0);
  if (!width || !height || width === height) return "unknown";
  return height > width ? "portrait" : "landscape";
};

export const proTrackMatchesOrientation = (trackOrSettings, orientation = "auto") => {
  void orientation;
  const actual = getProTrackOrientation(trackOrSettings);
  return actual !== "unknown";
};

export const normalizeProAudioMode = () => PRO_AUDIO_MODE_RAW;

export const getProAudioCaptureOptions = () => ({
  channelCount: 2,
  sampleRate: 48_000,
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
  voiceIsolation: false,
});

export const getProAudioPublishOptions = () => ({
  audioPreset: { maxBitrate: 192_000, priority: "high" },
  forceStereo: true,
  dtx: false,
  red: true,
});

export const getProPublishDefaults = (
  profile = PRO_VIDEO_PROFILE_HD,
  audioMode = PRO_AUDIO_MODE_RAW,
) => {
  const fullHd = profile === PRO_VIDEO_PROFILE_FULL_HD;
  const audioDefaults = getProAudioPublishOptions(audioMode);
  return ({
  simulcast: true,
  videoCodec: "h264",
  backupCodec: false,
  ...audioDefaults,
  // Preserve motion and audio continuity under congestion. Simulcast lowers
  // picture detail before the browser begins dropping whole video frames.
  degradationPreference: "maintain-framerate",
  videoEncoding: {
    maxBitrate: fullHd ? PRO_MODE_FULL_HD_MAX_BITRATE : PRO_MODE_MAX_BITRATE,
    maxFramerate: PRO_MODE_FRAME_RATE,
    priority: "high",
  },
  // Explicit layers prevent every viewer from receiving the primary camera
  // encoding when their screen or connection only needs a smaller rendition.
  videoSimulcastLayers: fullHd
    ? [
        { width: 320, height: 180, encoding: { maxBitrate: 250_000, maxFramerate: 20 } },
        { width: 960, height: 540, encoding: { maxBitrate: 1_200_000, maxFramerate: 30 } },
      ]
    : [
        { width: 320, height: 180, encoding: { maxBitrate: 250_000, maxFramerate: 20 } },
        { width: 640, height: 360, encoding: { maxBitrate: 700_000, maxFramerate: 24 } },
      ],
  });
};

export const getProViewerSubscription = ({
  participantIdentity,
  activeIdentity,
  transitionIdentities = [],
}) => {
  const transitionSet = new Set(transitionIdentities.filter(Boolean));
  if (participantIdentity === activeIdentity) {
    return { subscribed: true, quality: "high", fps: PRO_MODE_FRAME_RATE };
  }
  if (transitionSet.has(participantIdentity)) {
    return { subscribed: true, quality: "high", fps: PRO_MODE_FRAME_RATE };
  }
  return { subscribed: false, quality: "low", fps: 0 };
};

export const getProControlSubscription = ({
  participantIdentity,
  activeIdentity,
  previewIdentity,
  transitionIdentities = [],
}) => {
  const transitionSet = new Set(transitionIdentities.filter(Boolean));
  const isLargeMonitor = participantIdentity === activeIdentity
    || participantIdentity === previewIdentity
    || transitionSet.has(participantIdentity);
  return isLargeMonitor
    ? { subscribed: true, quality: "high", fps: PRO_MODE_FRAME_RATE }
    : { subscribed: true, quality: "low", fps: 6 };
};
